import { Constants } from '../internal/Constants';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo } from 'react';
import type { IAssetDTO } from '@app/engine';

/** File-local constants for the ThemeProvider. */
const localConstants = {
  theme: 'main'
} as const;

/** Navigation context type definition. */
interface IThemeContextType {
  theme: string;
  getThemedAssetDTO: (bundle: string, asset: string) => IAssetDTO;
  getCommonAssetDTO: (bundle: string, asset: string) => IAssetDTO;
}

/** Navigation context instance. */
const ThemeContext = createContext<IThemeContextType | undefined>(undefined);

/**
 * @param root0
 * @param root0.children The child components to be wrapped by the ThemeProvider.
 * @returns A JSX element wrapping children with ThemeContext provider.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const theme = localConstants.theme;

  // Update theme root css class when theme changes
  useEffect(() => {
    const rootEl = document.getElementById('root');
    if (!rootEl) return;

    const themeClassName = `${theme}${Constants.ThemeSuffix}`;
    Array.from(rootEl.classList)
      .filter((cls) => cls.endsWith(Constants.ThemeSuffix))
      .forEach((cls) => rootEl.classList.remove(cls));

    rootEl.classList.add(themeClassName);
  }, [theme]);

  // Themed asset DTO generator
  const getThemedAssetDTO = useCallback(
    (bundle: string, asset: string): IAssetDTO => {
      return { manifest: Constants.Manifest, bundle: `${theme}-${bundle}`, asset };
    },
    [theme]
  );

  // Common assets not tied to theme
  const getCommonAssetDTO = useCallback((bundle: string, asset: string): IAssetDTO => {
    return { manifest: Constants.Manifest, bundle, asset };
  }, []);

  // Memoized context value
  const value = useMemo(() => {
    return {
      theme,
      getThemedAssetDTO,
      getCommonAssetDTO
    };
  }, [theme, getThemedAssetDTO, getCommonAssetDTO]);

  // Render provider
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Custom hook to access navigation context.
 *
 * @returns The navigation context value.
 * @throws Error if used outside of NavProvider.
 */
export function useTheme(): IThemeContextType {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
